


import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  clearSelectedAuditLog,
  fetchAuditLogById,
  fetchAuditLogs,
  fetchAuditLogSettings,
  updateAuditLogRetention,
  updateAuditLogStatus,
} from "../../features/admin/AuditLogSlice";

const MODULE_OPTIONS = [
  ["AUTH", "Xác thực"],
  ["USER", "Người dùng"],
  ["COMPANY", "Hội viên"],
  ["ROLE", "Chức vụ"],
  ["PERMISSION", "Phân quyền"],
  ["ORDER", "Giao dịch"],
  ["MEMBERSHIP_FEE", "Hội phí"],
  ["POINT", "Điểm hoạt động"],
  ["PRODUCT", "Sản phẩm"],
  ["POST", "Bài viết"],
  ["COUPON", "Mã thưởng"],
  ["EVENT_REGISTRATION", "Đăng ký hoạt động"],
  ["SPONSOR", "Nhà tài trợ"],
  ["COMMENT", "Bình luận"],
  ["AUDIT_LOG", "Nhật ký hệ thống"],
];

const ACTION_OPTIONS = [
  ["CREATE", "Tạo mới"],
  ["UPDATE", "Cập nhật"],
  ["DELETE", "Xóa"],
  ["APPROVE", "Duyệt"],
  ["REJECT", "Từ chối"],
  ["LOGIN", "Đăng nhập"],
  ["LOGOUT", "Đăng xuất"],
  ["STATUS_UPDATE", "Đổi trạng thái"],
  ["LOCK_UPDATE", "Khóa/mở khóa"],
  ["PERMISSION_UPDATE", "Cập nhật quyền"],
  ["POINT_UPDATE", "Cập nhật điểm"],
  ["SETTING_UPDATE", "Cập nhật cấu hình"],
  ["RESET_PASSWORD", "Đặt lại mật khẩu"],
  ["SEND_PASSWORD_RESET_OTP", "Gửi OTP"],
  ["SEND_REMINDER_EMAIL", "Gửi email nhắc phí"],
  ["PAYMENT_UPDATE", "Cập nhật thanh toán"],
  ["ROLE_UPDATE", "Cập nhật chức vụ"],
  ["USE", "Sử dụng"],
  ["AUTO_DELETE", "Tự động dọn log"],
];

const RETENTION_OPTIONS = [
  [0, "Không tự động xóa"],
  [1, "1 ngày"],
  [3, "3 ngày"],
  [7, "7 ngày"],
  [30, "30 ngày"],
  [60, "60 ngày"],
  [90, "90 ngày"],
  [180, "180 ngày"],
  [365, "365 ngày"],
];

const INITIAL_FILTERS = {
  search: "",
  module: "",
  action: "",
  status: "",
  source: "",
  from: "",
  to: "",
  entityType: "",
  entityId: "",
};

const ENTITY_OPTIONS = {
  User: { label: "Người dùng", path: "/admin/users" },
  Company: { label: "Hội viên", path: "/admin/partner" },
  Role: { label: "Chức vụ", path: "/admin/roles" },
  Permission: { label: "Quyền hệ thống", path: "/admin/permission-show" },
  Order: { label: "Giao dịch", path: "/admin/order" },
  MembershipFee: { label: "Hội phí", path: "/admin/membershipfees" },
  MembershipFeePayment: {
    label: "Lần đóng hội phí",
    path: "/admin/membershipfees",
  },
  PointLog: { label: "Lịch sử điểm", path: "/admin/point-log" },
  Product: { label: "Sản phẩm", path: "/admin/product" },
  Post: { label: "Bài viết", path: "/admin/posts" },
  Coupon: { label: "Mã thưởng", path: "/admin/coupon" },
  EventRegistration: {
    label: "Đăng ký hoạt động",
    path: "/admin/registration",
  },
  Sponsor: { label: "Nhà tài trợ", path: "/admin/sponsors" },
  Comment: { label: "Bình luận", path: "/admin/feedback" },
  AuditLog: { label: "Nhật ký hệ thống", path: "/admin/audit-logs" },
  AUDIT_LOG_SETTING: {
    label: "Cấu hình nhật ký",
    path: "/admin/audit-logs",
  },
};

const FIELD_LABELS = {
  id: "Mã",
  items: "Danh sách dữ liệu",
  enabled: "Trạng thái ghi nhật ký",
  retentionDays: "Thời hạn lưu (ngày)",
  status: "Trạng thái",
  approvalStatus: "Trạng thái duyệt",
  approveStatus: "Trạng thái duyệt",
  isApproved: "Đã duyệt",
  isLocked: "Trạng thái khóa",
  active: "Đang hoạt động",
  userFullName: "Họ và tên",
  userCode: "Mã người dùng",
  userEmail: "Email",
  userPhone: "Số điện thoại",
  account: "Tài khoản",
  roleId: "Chức vụ",
  roleName: "Tên chức vụ",
  permissionIds: "Danh sách quyền",
  permissionName: "Tên quyền",
  permissionSlug: "Mã quyền",
  companyName: "Tên hội viên",
  companyId: "Hội viên",
  point: "Điểm hoạt động",
  title: "Tiêu đề",
  name: "Tên",
  slug: "Đường dẫn",
  content: "Nội dung",
  description: "Mô tả",
  totalPrice: "Tổng tiền",
  fee: "Số tiền hội phí",
  payment: "Thanh toán",
  reason: "Lý do",
  startDate: "Ngày bắt đầu",
  endDate: "Ngày kết thúc",
  payStartDay: "Ngày bắt đầu đóng",
  payEndDay: "Ngày kết thúc đóng",
  createdAt: "Ngày tạo",
  updatedAt: "Ngày cập nhật",
  deletedAt: "Ngày xóa",
  authenticated: "Trạng thái đăng nhập",
  method: "Phương thức",
  recipient: "Người nhận",
  deletedCount: "Số bản ghi đã xóa",
  setting: "Cấu hình thay đổi",
};

const SOURCE_LABELS = {
  API: "Thao tác trên hệ thống",
  CRON: "Tác vụ định kỳ",
  JOB: "Tác vụ nền",
  SYSTEM: "Hệ thống tự động",
};

const VALUE_LABELS = {
  SUCCESS: "Thành công",
  FAILED: "Thất bại",
  pending: "Chờ xử lý",
  approved: "Đã duyệt",
  rejected: "Đã từ chối",
  published: "Đã xuất bản",
  draft: "Bản nháp",
  feedback: "Đã phản hồi",
  active: "Đang hoạt động",
  inactive: "Ngừng hoạt động",
  locked: "Đã khóa",
  unlocked: "Đã mở khóa",
  "[REDACTED]": "Đã ẩn dữ liệu nhạy cảm",
};

const getUserPermissions = () => {
  try {
    const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
    return permissions.map((permission) => permission.permissionSlug);
  } catch (error) {
    return [];
  }
};

const hasPermission = (requiredPermission) => {
  if (localStorage.getItem("userId") == 1) return true;
  return getUserPermissions().includes(requiredPermission);
};

const humanizeKey = (value) => {
  if (!value) return "-";
  if (FIELD_LABELS[value]) return FIELD_LABELS[value];

  const words = String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

const getLabel = (options, value) =>
  options.find(([key]) => key === value)?.[1] || humanizeKey(value);

const getEntityInfo = (entityType) =>
  ENTITY_OPTIONS[entityType] || {
    label: humanizeKey(entityType),
    path: null,
  };

const getErrorMessage = (error, fallback) => {
  const message = error?.messages || error?.message;
  if (typeof message === "string") return message;
  if (message && typeof message === "object") {
    return Object.values(message)[0] || fallback;
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const normalizeJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const translateObjectKeys = (value) => {
  if (Array.isArray(value)) return value.map(translateObjectKeys);
  if (!value || typeof value !== "object") return value;

  return Object.entries(value).reduce((result, [key, item]) => {
    result[humanizeKey(key)] = translateObjectKeys(item);
    return result;
  }, {});
};

const formatValue = (value) => {
  if (value === undefined) return "Không có";
  if (value === null || value === "") return "Trống";
  if (typeof value === "boolean") return value ? "Có" : "Không";
  if (typeof value === "string" && VALUE_LABELS[value] !== undefined) {
    return VALUE_LABELS[value];
  }
  if (typeof value === "object") {
    return JSON.stringify(translateObjectKeys(value), null, 2);
  }
  return String(value);
};

const getChangedFields = (value) => {
  const normalizedValue = normalizeJson(value);
  if (Array.isArray(normalizedValue)) return normalizedValue;
  return normalizedValue ? [String(normalizedValue)] : [];
};

const ReadableDataViewer = ({ title, value }) => {
  const normalizedValue = normalizeJson(value);
  const hasData = normalizedValue !== null && normalizedValue !== undefined;

  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white">
      <p className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
        {title}
      </p>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words bg-slate-50 p-4 text-xs leading-5 text-slate-700">
        {hasData ? formatValue(normalizedValue) : "Không có dữ liệu"}
      </pre>
    </div>
  );
};

const DiffViewer = ({ beforeData, afterData }) => {
  const before = normalizeJson(beforeData);
  const after = normalizeJson(afterData);
  const beforeObject =
    before && typeof before === "object" && !Array.isArray(before) ? before : {};
  const afterObject =
    after && typeof after === "object" && !Array.isArray(after) ? after : {};
  const keys = [...new Set([...Object.keys(beforeObject), ...Object.keys(afterObject)])];

  if (keys.length === 0) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <ReadableDataViewer title="Trước thay đổi" value={before} />
        <ReadableDataViewer title="Sau thay đổi" value={after} />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div className="grid grid-cols-[minmax(8rem,0.7fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <div className="px-4 py-3">Trường dữ liệu</div>
        <div className="border-l border-slate-200 px-4 py-3 text-red-700">
          Trước thay đổi
        </div>
        <div className="border-l border-slate-200 px-4 py-3 text-emerald-700">
          Sau thay đổi
        </div>
      </div>
      <div className="max-h-[28rem] overflow-auto">
        {keys.map((key) => {
          const hasBefore = Object.prototype.hasOwnProperty.call(beforeObject, key);
          const hasAfter = Object.prototype.hasOwnProperty.call(afterObject, key);
          const isChanged =
            JSON.stringify(beforeObject[key]) !== JSON.stringify(afterObject[key]);

          return (
            <div
              key={key}
              className="grid grid-cols-[minmax(8rem,0.7fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-slate-100 last:border-b-0"
            >
              <div className="px-4 py-3">
                <p className="text-sm font-semibold text-slate-700">
                  {humanizeKey(key)}
                </p>
              </div>
              <pre
                className={`whitespace-pre-wrap break-words border-l px-4 py-3 text-xs leading-5 ${
                  isChanged
                    ? "border-red-200 bg-red-50 text-red-800"
                    : "border-slate-200 bg-white text-slate-500"
                }`}
              >
                {isChanged && <span className="mr-2 font-bold">−</span>}
                {hasBefore ? formatValue(beforeObject[key]) : "Không có"}
              </pre>
              <pre
                className={`whitespace-pre-wrap break-words border-l px-4 py-3 text-xs leading-5 ${
                  isChanged
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-500"
                }`}
              >
                {isChanged && <span className="mr-2 font-bold">+</span>}
                {hasAfter ? formatValue(afterObject[key]) : "Không có"}
              </pre>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EntityLink = ({ log, showId = true }) => {
  const entity = getEntityInfo(log.entityType);
  const canOpen = Boolean(entity.path && log.entityType !== "AUDIT_LOG_SETTING");
  const content = (
    <>
      <span>{entity.label}</span>
      {canOpen && <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />}
    </>
  );

  return (
    <div>
      {canOpen ? (
        <Link
          to={entity.path}
          className="inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-900 hover:underline"
          title={`Mở màn hình ${entity.label.toLowerCase()}`}
        >
          {content}
        </Link>
      ) : (
        <p className="font-medium text-slate-700">{content}</p>
      )}
      {showId && (
        <p className="mt-1 text-xs text-slate-500">Mã: {log.entityId || "-"}</p>
      )}
    </div>
  );
};

const AuditLogs = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    auditLogs,
    totalItems,
    totalPage,
    currentPage,
    selectedAuditLog,
    settings,
    listStatus,
    detailStatus,
    settingsStatus,
    updateStatus,
    error,
    detailError,
  } = useSelector((state) => state.auditLogs);

  const urlFilters = useMemo(
    () => ({
      ...INITIAL_FILTERS,
      search: searchParams.get("search") || "",
      module: searchParams.get("module") || "",
      action: searchParams.get("action") || "",
      status: searchParams.get("status") || "",
      source: searchParams.get("source") || "",
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
      entityType: searchParams.get("entityType") || "",
      entityId: searchParams.get("entityId") || "",
    }),
    []
  );
  const [filters, setFilters] = useState(urlFilters);
  const [appliedFilters, setAppliedFilters] = useState(urlFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [retentionDays, setRetentionDays] = useState(30);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const canUpdateRetention = hasPermission("AUDIT_LOG_SETTING_UPDATE");
  const canUpdateStatus = hasPermission("AUDIT_LOG_STATUS_UPDATE");

  useEffect(() => {
    const params = Object.entries({
      ...appliedFilters,
      page,
      limit,
      to: appliedFilters.to
        ? `${appliedFilters.to}T23:59:59.999`
        : "",
    }).reduce((result, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        result[key] = value;
      }
      return result;
    }, {});

    dispatch(fetchAuditLogs(params));
  }, [dispatch, appliedFilters, page, limit]);

  useEffect(() => {
    dispatch(fetchAuditLogSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings?.retentionDays !== undefined) {
      setRetentionDays(settings.retentionDays);
    }
  }, [settings]);

  useEffect(() => {
    if (totalPage > 0 && page > totalPage) setPage(totalPage);
  }, [page, totalPage]);

  const visiblePages = useMemo(() => {
    const pageCount = Math.max(totalPage || 1, 1);
    const start = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
    const end = Math.min(pageCount, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [currentPage, totalPage]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleFilter = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
    const nextParams = Object.entries(filters).reduce(
      (result, [key, value]) => {
        if (value) result[key] = value;
        return result;
      },
      {}
    );
    setSearchParams(nextParams, { replace: true });
  };

  const handleResetFilter = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const handleClearEntityFilter = () => {
    const nextFilters = { ...filters, entityType: "", entityId: "" };
    const nextAppliedFilters = {
      ...appliedFilters,
      entityType: "",
      entityId: "",
    };
    setFilters(nextFilters);
    setAppliedFilters(nextAppliedFilters);
    setPage(1);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("entityType");
    nextParams.delete("entityId");
    setSearchParams(nextParams, { replace: true });
  };

  const handleRefresh = () => {
    dispatch(fetchAuditLogs({
      ...Object.entries(appliedFilters).reduce((result, [key, value]) => {
        if (value) result[key] = value;
        return result;
      }, {}),
      page,
      limit,
      ...(appliedFilters.to
        ? { to: `${appliedFilters.to}T23:59:59.999` }
        : {}),
    }));
  };

  const handleOpenDetail = (id) => {
    setIsDetailOpen(true);
    dispatch(fetchAuditLogById(id));
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    dispatch(clearSelectedAuditLog());
  };

  const handleSaveRetention = async () => {
    try {
      await dispatch(updateAuditLogRetention(Number(retentionDays))).unwrap();
      toast.success("Đã cập nhật thời hạn lưu nhật ký.");
      handleRefresh();
    } catch (saveError) {
      toast.error(
        getErrorMessage(saveError, "Không thể cập nhật thời hạn lưu nhật ký.")
      );
    }
  };

  const handleToggleStatus = async () => {
    const nextEnabled = !settings?.enabled;
    if (
      !nextEnabled &&
      !window.confirm(
        "Tắt ghi nhật ký sẽ ngừng lưu các thao tác nghiệp vụ mới. Bạn vẫn muốn tiếp tục?"
      )
    ) {
      return;
    }

    try {
      await dispatch(updateAuditLogStatus(nextEnabled)).unwrap();
      toast.success(
        nextEnabled ? "Đã bật ghi nhật ký." : "Đã tắt ghi nhật ký."
      );
      handleRefresh();
    } catch (saveError) {
      toast.error(
        getErrorMessage(saveError, "Không thể cập nhật trạng thái ghi nhật ký.")
      );
    }
  };

  return (
    <div className="min-h-full rounded-2xl bg-slate-50/70 p-3 sm:p-5 lg:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-blue-600 p-3 text-white shadow-lg shadow-blue-600/20">
            <ShieldCheckIcon className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Nhật ký hệ thống
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Theo dõi thao tác, thay đổi dữ liệu và sự kiện phát sinh trong hệ thống.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={listStatus === "loading"}
          className="btn btn-outline btn-sm w-fit rounded-xl border-slate-300 bg-white"
        >
          <ArrowPathIcon
            className={`h-4 w-4 ${listStatus === "loading" ? "animate-spin" : ""}`}
          />
          Làm mới
        </button>
      </div>

      <div className="mb-6 grid gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Tổng nhật ký</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">
                {totalItems.toLocaleString("vi-VN")}
              </p>
            </div>
            <div className="rounded-2xl bg-blue-50 p-3 text-blue-600">
              <DocumentMagnifyingGlassIcon className="h-7 w-7" />
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Theo bộ lọc đang áp dụng
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">Trạng thái ghi log</p>
              <div className="mt-2 flex items-center gap-2">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    settings?.enabled ? "bg-emerald-500" : "bg-slate-400"
                  }`}
                />
                <p className="text-lg font-bold text-slate-900">
                  {settingsStatus === "loading"
                    ? "Đang tải..."
                    : settings?.enabled
                      ? "Đang bật"
                      : "Đang tắt"}
                </p>
              </div>
            </div>
            {canUpdateStatus && settings && (
              <input
                type="checkbox"
                className="toggle toggle-success"
                checked={Boolean(settings.enabled)}
                disabled={updateStatus === "loading"}
                onChange={handleToggleStatus}
                aria-label="Bật hoặc tắt ghi nhật ký"
              />
            )}
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Thao tác bật/tắt luôn được lưu lại
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0 flex-1">
              <label className="mb-2 block text-sm font-medium text-slate-500">
                Thời hạn lưu
              </label>
              <select
                value={retentionDays}
                onChange={(event) => setRetentionDays(Number(event.target.value))}
                disabled={!canUpdateRetention || updateStatus === "loading"}
                className="select select-bordered select-sm w-full rounded-xl bg-white"
              >
                {RETENTION_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {canUpdateRetention && (
              <button
                type="button"
                onClick={handleSaveRetention}
                disabled={
                  updateStatus === "loading" ||
                  !settings ||
                  Number(retentionDays) === Number(settings?.retentionDays)
                }
                className="btn btn-primary btn-sm rounded-xl"
              >
                Lưu
              </button>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Log hết hạn được hệ thống tự động dọn
          </p>
        </div>
      </div>

      <form
        onSubmit={handleFilter}
        className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
      >
        <div className="mb-4 flex items-center gap-2">
          <FunnelIcon className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-slate-800">Bộ lọc nhật ký</h2>
        </div>
        {appliedFilters.entityId && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <span>Đang xem lịch sử của</span>
            <span className="font-semibold">
              {getEntityInfo(appliedFilters.entityType).label} #{appliedFilters.entityId}
            </span>
            <button
              type="button"
              onClick={handleClearEntityFilter}
              className="ml-auto inline-flex items-center gap-1 font-medium hover:text-blue-950"
            >
              <XMarkIcon className="h-4 w-4" />
              Bỏ lọc đối tượng
            </button>
          </div>
        )}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="relative xl:col-span-2">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-slate-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              placeholder="Người thực hiện, tài khoản, mã đối tượng, request ID..."
              className="input input-bordered w-full rounded-xl bg-white pl-10"
            />
          </label>
          <select
            value={filters.module}
            onChange={(event) => updateFilter("module", event.target.value)}
            className="select select-bordered w-full rounded-xl bg-white"
          >
            <option value="">Tất cả module</option>
            {MODULE_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={filters.action}
            onChange={(event) => updateFilter("action", event.target.value)}
            className="select select-bordered w-full rounded-xl bg-white"
          >
            <option value="">Tất cả thao tác</option>
            {ACTION_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
            className="select select-bordered w-full rounded-xl bg-white"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="SUCCESS">Thành công</option>
            <option value="FAILED">Thất bại</option>
          </select>
          <select
            value={filters.source}
            onChange={(event) => updateFilter("source", event.target.value)}
            className="select select-bordered w-full rounded-xl bg-white"
          >
            <option value="">Tất cả nguồn</option>
            {Object.entries(SOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <label>
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Từ ngày
            </span>
            <input
              type="date"
              value={filters.from}
              onChange={(event) => updateFilter("from", event.target.value)}
              className="input input-bordered w-full rounded-xl bg-white"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Đến ngày
            </span>
            <input
              type="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(event) => updateFilter("to", event.target.value)}
              className="input input-bordered w-full rounded-xl bg-white"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={handleResetFilter}
            className="btn btn-ghost btn-sm rounded-xl"
          >
            Xóa lọc
          </button>
          <button type="submit" className="btn btn-primary btn-sm rounded-xl">
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            Áp dụng
          </button>
        </div>
      </form>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 sm:px-5">
          <div>
            <h2 className="font-semibold text-slate-900">Danh sách nhật ký</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Trang {currentPage || 1} / {totalPage || 1}
            </p>
          </div>
          {listStatus === "loading" && (
            <span className="flex items-center gap-2 text-sm text-blue-600">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Đang tải
            </span>
          )}
        </div>

        {listStatus === "failed" ? (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {getErrorMessage(error, "Không thể tải danh sách nhật ký.")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th>Thời gian</th>
                  <th>Người thực hiện</th>
                  <th>Module / thao tác</th>
                  <th>Đối tượng</th>
                  <th>Trạng thái</th>
                  <th className="text-center">Chi tiết</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((item) => (
                  <tr key={item.id} className="hover">
                    <td className="whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm">
                        <ClockIcon className="h-4 w-4 text-slate-400" />
                        {formatDateTime(item.occurredAt)}
                      </div>
                      <p className="mt-1 text-xs text-slate-400">#{item.id}</p>
                    </td>
                    <td>
                      <p className="max-w-48 truncate font-medium text-slate-800">
                        {item.actorName || "Hệ thống"}
                      </p>
                      <p className="max-w-48 truncate text-xs text-slate-500">
                        {item.actorAccount || item.actorRole || "-"}
                      </p>
                    </td>
                    <td>
                      <p className="font-medium text-slate-800">
                        {getLabel(MODULE_OPTIONS, item.module)}
                      </p>
                      <span className="mt-1 inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        {getLabel(ACTION_OPTIONS, item.action)}
                      </span>
                    </td>
                    <td>
                      <EntityLink log={item} />
                    </td>
                    <td>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          item.status === "SUCCESS"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {item.status === "SUCCESS" ? (
                          <CheckCircleIcon className="h-4 w-4" />
                        ) : (
                          <ExclamationTriangleIcon className="h-4 w-4" />
                        )}
                        {item.status === "SUCCESS" ? "Thành công" : "Thất bại"}
                      </span>
                      <p className="mt-1 text-xs text-slate-400">
                        {SOURCE_LABELS[item.source] || humanizeKey(item.source || "API")}
                      </p>
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        onClick={() => handleOpenDetail(item.id)}
                        className="btn btn-ghost btn-sm rounded-xl text-blue-600 hover:bg-blue-50"
                        title="Xem chi tiết"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {listStatus !== "loading" && auditLogs.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-16 text-center">
                      <DocumentMagnifyingGlassIcon className="mx-auto h-10 w-10 text-slate-300" />
                      <p className="mt-3 font-medium text-slate-600">
                        Chưa có nhật ký phù hợp
                      </p>
                      <p className="mt-1 text-sm text-slate-400">
                        Thử thay đổi hoặc xóa bộ lọc đang áp dụng.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-4 lg:flex-row lg:items-center lg:justify-between sm:px-5">
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
            <span>
              {totalItems > 0
                ? `Hiển thị ${(currentPage - 1) * limit + 1} - ${Math.min(
                    currentPage * limit,
                    totalItems
                  )} trong ${totalItems} bản ghi`
                : "Chưa có bản ghi"}
            </span>
            <label className="flex items-center gap-2">
              <span>Số dòng:</span>
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="select select-bordered select-sm rounded-lg bg-white"
              >
                {[10, 20, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={currentPage <= 1 || listStatus === "loading"}
                className="btn btn-ghost btn-sm btn-square rounded-xl"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              {visiblePages.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => setPage(pageNumber)}
                  disabled={listStatus === "loading"}
                  className={`btn btn-sm btn-square rounded-xl ${
                    pageNumber === currentPage
                      ? "btn-primary"
                      : "btn-ghost"
                  }`}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() =>
                  setPage((value) => Math.min(Math.max(totalPage || 1, 1), value + 1))
                }
                disabled={
                  currentPage >= Math.max(totalPage || 1, 1) ||
                  listStatus === "loading"
                }
                className="btn btn-ghost btn-sm btn-square rounded-xl"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
          </div>
        </div>
      </div>

      {isDetailOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) handleCloseDetail();
          }}
        >
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-6">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  Chi tiết nhật ký
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {selectedAuditLog ? `Mã nhật ký #${selectedAuditLog.id}` : "Đang tải dữ liệu"}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseDetail}
                className="btn btn-ghost btn-sm btn-square rounded-xl"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-5 sm:p-6">
              {detailStatus === "loading" && (
                <div className="flex min-h-72 items-center justify-center gap-3 text-blue-600">
                  <ArrowPathIcon className="h-6 w-6 animate-spin" />
                  Đang tải chi tiết nhật ký...
                </div>
              )}

              {detailStatus === "failed" && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                  {getErrorMessage(detailError, "Không thể tải chi tiết nhật ký.")}
                </div>
              )}

              {selectedAuditLog && (
                <div className="space-y-6">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                      ["Thời gian", formatDateTime(selectedAuditLog.occurredAt)],
                      ["Người thực hiện", selectedAuditLog.actorName || "Hệ thống"],
                      ["Tài khoản", selectedAuditLog.actorAccount || "-"],
                      ["Vai trò", selectedAuditLog.actorRole || "-"],
                      ["Module", getLabel(MODULE_OPTIONS, selectedAuditLog.module)],
                      ["Thao tác", getLabel(ACTION_OPTIONS, selectedAuditLog.action)],
                      ["Đối tượng", <EntityLink log={selectedAuditLog} showId={false} />],
                      ["Mã đối tượng", selectedAuditLog.entityId || "-"],
                      ["Trạng thái", selectedAuditLog.status === "SUCCESS" ? "Thành công" : "Thất bại"],
                      [
                        "Nguồn",
                        SOURCE_LABELS[selectedAuditLog.source] ||
                          humanizeKey(selectedAuditLog.source || "API"),
                      ],
                      ["IP", selectedAuditLog.ipAddress || "-"],
                      ["Request ID", selectedAuditLog.requestId || "-"],
                    ].map(([label, value], index) => (
                      <div key={`${label}-${index}`} className="rounded-xl bg-slate-50 p-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                          {label}
                        </p>
                        <div className="mt-1 break-words text-sm font-semibold text-slate-800">
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedAuditLog.reason && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                      <p className="text-sm font-semibold text-red-700">Lý do / lỗi</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-red-600">
                        {selectedAuditLog.reason}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="mb-2 text-sm font-semibold text-slate-700">
                      Trường thay đổi
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {getChangedFields(selectedAuditLog.changedFields).length > 0 ? (
                        getChangedFields(selectedAuditLog.changedFields).map((field) => (
                          <span
                            key={field}
                            className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700"
                          >
                            {humanizeKey(field)}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400">Không có dữ liệu thay đổi</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-700">
                        So sánh dữ liệu
                      </p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-1 text-red-700">
                          <span className="h-2.5 w-2.5 rounded-sm bg-red-200" />
                          Dữ liệu cũ
                        </span>
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-200" />
                          Dữ liệu mới
                        </span>
                      </div>
                    </div>
                    <DiffViewer
                      beforeData={selectedAuditLog.beforeData}
                      afterData={selectedAuditLog.afterData}
                    />
                  </div>

                  <ReadableDataViewer
                    title="Thông tin bổ sung"
                    value={selectedAuditLog.metadata}
                  />

                  {selectedAuditLog.userAgent && (
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        Trình duyệt / thiết bị
                      </p>
                      <p className="mt-1 break-all text-sm text-slate-600">
                        {selectedAuditLog.userAgent}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;


